import { assertUnreachable } from '@sgme/fp';
import { Button, HStack } from '@sgme/ui';
import { FormattedMessage } from 'react-intl';

import { useOptionSelector } from '@/App/tiles/Option/OptionContext.ts';
import { useCheckOption } from '@/App/tiles/Option/QueueContext.tsx';
import { SgAmountInput } from '@/components/sg-design-system/components/input/SgAmountInput';
import {
  getQuote,
  selectOptionCurrency,
  selectOptionLegsProperties,
} from '@/store/option/selectors/optionSelectors.ts';
import type { LegId, OptionQuote, OptionSide } from '@/store/option/state.ts';
import type { CurrencyChoice } from '@/types/common.ts';

type CollapseStatus = 'collapsed' | 'expanded';

type CommercialMarginProps = {
  tileId: string;
  onClick: (collapseMarketParametersStatus: CollapseStatus) => void;
  expanded?: boolean;
};

export function CommercialMargin({ tileId, onClick, expanded }: CommercialMarginProps) {
  const status = expanded ? 'expanded' : 'collapsed';
  const [checkOption] = useCheckOption();

  const marginData = useOptionSelector((tile) => {
    const legId: LegId = `${tileId}/0`;

    const legsProperties = selectOptionLegsProperties(tile, legId);
    const quote = getQuote(tile);
    const side = legsProperties?.side ?? 'Buy';
    const optionCurrency = selectOptionCurrency(tile);

    const markupCurrencyLabel = tile.properties.markupCurrency;
    const markupCurrency: CurrencyChoice = markupCurrencyLabel === optionCurrency.currency2 ? 2 : 1;

    const strategyMargin = getStrategyMarginFromQuoteCcy(markupCurrency === 1 ? quote?.ccy1 : quote?.ccy2, side);

    return {
      optionCurrency,
      markupCurrency: markupCurrencyLabel,
      strategyMargin,
    };
  });

  const handleCollapseMarketParameters = () => {
    const newStatus = status === 'collapsed' ? 'expanded' : 'collapsed';
    onClick(newStatus);
  };

  function handleMarkupCurrencyChange() {
    if (marginData.markupCurrency === marginData.optionCurrency.currency2) {
      checkOption({ tileId, properties: { markupCurrency: marginData?.optionCurrency.currency1 } });
    } else {
      checkOption({ tileId, properties: { markupCurrency: marginData?.optionCurrency.currency2 } });
    }
  }

  return (
    <>
      <HStack verticalAlignment="center" fullWidth className="px-2">
        <div className="flex-fill border-top" style={{ height: 1 }} />

        <Button
          size="sm"
          purpose="flat"
          onClick={handleCollapseMarketParameters}
          data-e2e="commercial-margin-toggle-button"
        >
          <FormattedMessage id="option.sections.commercialMargin" />
          <i className="icon icon-sm">{status === 'collapsed' ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</i>
        </Button>

        <div className="flex-fill border-top" style={{ height: 1 }} />
      </HStack>

      {status === 'expanded' && (
        <div className="form-group form-group-sm d-flex align-items-center my-1 no-gutters">
          <div className="pe-1 col-form-label col-form-label-sm col-4 text-nowrap text-end">
            <FormattedMessage id="option.fields.marginAmount" />
          </div>
          <span className="col-7 d-flex justify-content-between" data-e2e="marginCurrency">
            <SgAmountInput
              data-e2e="commercialMargin"
              size="sm"
              value={marginData.strategyMargin}
              readOnly
              unit={marginData.markupCurrency}
              onUnitClick={handleMarkupCurrencyChange}
            />
          </span>
        </div>
      )}
    </>
  );
}

export function getStrategyMarginFromQuoteCcy(
  quote: OptionQuote['ccy1'] | undefined,
  side: OptionSide,
): number | undefined {
  switch (side) {
    case 'Buy':
      return quote?.askMarkup;
    case 'Sell':
      return quote?.bidMarkup;
    default:
      assertUnreachable(side, 'Unhandled side');
  }
}
